import { useEffect, useState } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { buildExternalMsal, clearPending, readPending } from '../auth/externalSso';
import { useAuth } from '../auth/AuthContext';

// MSAL redirect target for per-client external SSO. Completes the redirect,
// hands the resulting id/access tokens to the API, and lands the user on the
// portal home where they're now authenticated by session cookie.
export default function ExternalAuthCallback() {
  const { refreshClient } = useAuth();
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const pending = readPending();
        if (!pending) { setError('Sign-in context lost — please try again.'); return; }

        const pca = buildExternalMsal(pending.tenantId, pending.clientId);
        await pca.initialize();
        const result = await pca.handleRedirectPromise();
        if (!result) {
          // No redirect in progress — somebody hit this URL directly.
          setError('No sign-in in progress.');
          return;
        }
        if (!result.idToken || !result.accessToken) {
          setError('Microsoft sign-in didn’t return the expected tokens.');
          return;
        }

        const r = await fetch('/api/auth/external/sso', {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            id_token: result.idToken,
            access_token: result.accessToken,
            client_slug: pending.slug ?? undefined,
          }),
        });
        if (!r.ok) {
          const body = await r.json().catch(() => ({ error: 'sso_failed' }));
          setError(humanError(body.error));
          return;
        }

        clearPending();
        // Optional: clear MSAL's local cache so this device doesn't keep
        // long-lived Microsoft state we don't need post-sign-in.
        await pca.clearCache();

        await refreshClient();
        setDone(true);
      } catch (e) {
        setError(e instanceof Error ? e.message : String(e));
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (done) return <Navigate to="/" replace />;
  return (
    <div style={{ minHeight: '100vh', display: 'grid', placeItems: 'center', background: 'var(--mi-bg)' }}>
      <div className="card" style={{ width: 420 }}>
        <img src="/MI-Logo-LightBackground.png" alt="Mount Insights" style={{ width: '100%', maxWidth: 150, marginBottom: 12, display: 'block', marginLeft: 'auto', marginRight: 'auto' }} />
        {error ? (
          <>
            <h3 style={{ color: 'var(--mi-navy)', marginTop: 0 }}>We couldn’t finish signing you in</h3>
            <p style={{ color: 'var(--mi-muted)' }}>{error}</p>
            <Link to="/login" className="btn-primary" style={{ display: 'inline-block', padding: '6px 12px', borderRadius: 6, textDecoration: 'none', color: 'white' }}>Back to sign-in</Link>
          </>
        ) : (
          <p style={{ color: 'var(--mi-muted)', margin: 0 }}>Completing sign-in…</p>
        )}
      </div>
    </div>
  );
}

function humanError(code: string | undefined): string {
  switch (code) {
    case 'not_in_access_group': return 'Your account isn’t in the access group for this workspace. Ask your IT admin to add you.';
    case 'no_client_for_tenant': return 'Your Microsoft tenant isn’t linked to a workspace.';
    case 'sso_disabled_for_client': return 'Single sign-on isn’t enabled for this workspace.';
    case 'sso_not_configured': return 'Single sign-on isn’t configured on the server.';
    case 'graph_check_failed': return 'Couldn’t verify your group membership with Microsoft.';
    case 'invalid_token':
    case 'untrusted_audience':
    case 'untrusted_issuer':
    case 'malformed_token': return 'The Microsoft sign-in token couldn’t be validated.';
    default: return 'Sign-in failed.';
  }
}
